import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useSlotGroup,
} from '@vibesites/runtime/react';
import type { Floorplan } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';
import { parseBedCount } from '../utils/bedroomOptions';

// ============================================================================
// Formatters — mirror FloorplanShowcase so this spotlight matches the rest
// of the site's floor plan copy.
// ============================================================================

function toNumber(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw).replace(/[$,]/g, ''));
  return Number.isNaN(n) ? null : n;
}

/**
 * Property data occasionally comes through with literal placeholder strings
 * like "false", "null", or "n/a" where a real value is missing. Treat those
 * as empty so the UI never renders e.g. "Plan false".
 */
const UNUSABLE_PLACEHOLDERS = new Set([
  'false',
  'true',
  'null',
  'undefined',
  'none',
  'n/a',
  'na',
  '-',
  '0',
]);

function usableText(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (UNUSABLE_PLACEHOLDERS.has(trimmed.toLowerCase())) return '';
  return trimmed;
}

function formatBeds(plan: Floorplan | null): string | null {
  if (!plan) return null;
  const beds = parseBedCount(plan.floorplan_beds_min);
  if (beds == null) return null;
  return beds === 0 ? 'Studio' : `${beds} Bed`;
}

function formatBaths(plan: Floorplan | null): string | null {
  if (!plan) return null;
  const min = toNumber(plan.floorplan_baths_min);
  const max = toNumber(plan.floorplan_baths_max);
  if (min == null) return null;
  const value = max != null && max > min ? `${min} - ${max}` : `${min}`;
  return `${value} Bath`;
}

function formatSqft(plan: Floorplan | null): string | null {
  if (!plan) return null;
  const min = toNumber(plan.floorplan_sqft_min);
  const max = toNumber(plan.floorplan_sqft_max);
  if (min == null || min <= 0) return null;
  const value =
    max != null && max > min
      ? `${min.toLocaleString()} - ${max.toLocaleString()}`
      : min.toLocaleString();
  return `${value} Sq. Ft.`;
}

function formatPrice(plan: Floorplan | null): string | null {
  if (!plan) return null;
  const min = toNumber(plan.floorplan_price_min);
  if (min == null || min <= 0) return null;
  return `Starting at $${Math.round(min).toLocaleString()}`;
}

/**
 * Pick the floor plan the "Featured Floor Plan" page should spotlight.
 *
 * Priority:
 *   1) A plan explicitly flagged by the data (floorplan_available_units > 0 beats a dark unit).
 *   2) Otherwise the first floor plan on the property.
 */
function pickFeaturedPlan(plans: Floorplan[]): Floorplan | null {
  if (!plans.length) return null;
  const available = plans.find((p) => {
    const units = toNumber(p.floorplan_available_units);
    return units != null && units > 0;
  });
  return available || plans[0];
}

// ============================================================================
// Slot group definitions
// ============================================================================

/*
 * Four image slots so authors can show a floor plan drawing plus up to three
 * supporting shots (e.g. 3D render, color variant, staged photo). Slot 1
 * defaults to the first floor plan's image via the collection source binding;
 * slots 2-4 start empty. The thumbnail strip only renders filled slots.
 */
const image1Def: SlotGroupDefinition = {
  label: 'Floor Plan Image 1 (Main)',
  description:
    'Primary image shown in the spotlight. Defaults to the first floor plan image when left empty.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'floorplans[0].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_name',
      defaultValue: 'Featured floor plan layout',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 0,
    fieldMappings: { image: 'floorplan_image_url', alt: 'floorplan_name' },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Floor Plan Image 2',
  description: 'Optional second image (e.g. 3D render, color variant, staged photo).',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Floor Plan Image 3',
  description: 'Optional third image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
};

const image4Def: SlotGroupDefinition = {
  label: 'Floor Plan Image 4',
  description: 'Optional fourth image.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: '3D Tour',
  description:
    'Virtual-tour URL for this floor plan (Matterport, YouVisit, etc.). When filled, a "Take a 3D Tour" button appears and opens the tour in an in-page lightbox instead of linking out.',
  fields: {
    url: {
      label: 'Tour URL',
      type: 'url',
      defaultValue: '',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Take a 3D Tour',
    },
  },
};

const detailsDef: SlotGroupDefinition = {
  label: 'Floor Plan Details',
  description:
    'Name, label, and headline price for the featured floor plan. Leave empty to use the first floor plan from this property.',
  fields: {
    name: {
      label: 'Floor Plan Name',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_name',
      defaultValue: 'The Signature Floor Plan',
    },
    code: {
      label: 'Floor Plan Code',
      type: 'text',
      defaultField: 'floorplans[0].floorplan_code',
      defaultValue: '',
    },
    price_override: {
      label: 'Price (optional override)',
      type: 'text',
      defaultValue: '',
      description:
        'Overrides the auto-formatted starting price. Leave blank to show the first floor plan\'s computed starting price.',
    },
  },
  sourceBinding: {
    collection: 'floorplans',
    defaultIndex: 0,
    fieldMappings: { name: 'floorplan_name', code: 'floorplan_code' },
  },
};

const scheduleCtaDef: SlotGroupDefinition = {
  label: 'Schedule A Tour CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const viewAllCtaDef: SlotGroupDefinition = {
  label: 'View All Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function FeaturedFloorPlan() {
  useRevealStyles();
  const cardRef = useScrollReveal<HTMLDivElement>();
  const statsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 90 });
  const ctaRef = useScrollReveal<HTMLDivElement>();

  const floorplans = useCollectionBinding<Floorplan>('featuredfloorplan.items', {
    collection: 'floorplans',
  });
  const featured = useMemo(() => pickFeaturedPlan(floorplans ?? []), [floorplans]);

  const image1 = useSlotGroup('featuredfloorplan.image_1', image1Def);
  const image2 = useSlotGroup('featuredfloorplan.image_2', image2Def);
  const image3 = useSlotGroup('featuredfloorplan.image_3', image3Def);
  const image4 = useSlotGroup('featuredfloorplan.image_4', image4Def);
  const details = useSlotGroup('featuredfloorplan.details', detailsDef);
  const scheduleCta = useSlotGroup('featuredfloorplan.schedule_cta', scheduleCtaDef);
  const viewAllCta = useSlotGroup('featuredfloorplan.view_all_cta', viewAllCtaDef);
  const tourCta = useSlotGroup('featuredfloorplan.tour_cta', tourCtaDef);

  const planName =
    usableText(details.name) ||
    usableText(featured?.floorplan_name) ||
    'Featured Floor Plan';
  const planCode = usableText(details.code);

  const priceOverride = (details.price_override || '').trim();
  const price = priceOverride || formatPrice(featured) || 'Call for Pricing';

  /*
   * Collect filled image slots (keep slot order so the editor's numbered slots
   * map 1:1 to the thumbnail positions). Slot 1 falls back to the plan's own
   * floorplan_image_url so the main image is never empty when a plan exists.
   */
  const images = useMemo(() => {
    const planImage = featured?.floorplan_image_url || '';
    return [
      {
        slotIndex: 0 as 0 | 1 | 2 | 3,
        src: (image1.image && image1.image.trim()) || planImage,
        alt: (image1.alt && image1.alt.trim()) || `${planName} floor plan`,
      },
      {
        slotIndex: 1 as 0 | 1 | 2 | 3,
        src: (image2.image || '').trim(),
        alt: (image2.alt || '').trim() || `${planName} — view 2`,
      },
      {
        slotIndex: 2 as 0 | 1 | 2 | 3,
        src: (image3.image || '').trim(),
        alt: (image3.alt || '').trim() || `${planName} — view 3`,
      },
      {
        slotIndex: 3 as 0 | 1 | 2 | 3,
        src: (image4.image || '').trim(),
        alt: (image4.alt || '').trim() || `${planName} — view 4`,
      },
    ].filter((img) => Boolean(img.src));
  }, [image1, image2, image3, image4, featured, planName]);

  const [activeImage, setActiveImage] = useState(0);
  // If the editor removes/reorders images and the active index becomes invalid,
  // snap back to the first image.
  useEffect(() => {
    if (activeImage >= images.length && images.length > 0) {
      setActiveImage(0);
    }
  }, [activeImage, images.length]);
  const current = images[Math.min(activeImage, Math.max(0, images.length - 1))] ?? null;

  // 3D tour lightbox
  const tourUrl = usableText(tourCta.url);
  const tourLabel = tourCta.label || 'Take a 3D Tour';
  const [tourOpen, setTourOpen] = useState(false);
  const closeTour = useCallback(() => setTourOpen(false), []);

  useEffect(() => {
    if (!tourOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTour();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [tourOpen, closeTour]);

  useEffect(() => {
    if (!tourOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [tourOpen]);

  const stats = useMemo(
    () =>
      (
        [
          { icon: 'bed', label: 'Bedrooms', value: formatBeds(featured) },
          { icon: 'bath', label: 'Bathrooms', value: formatBaths(featured) },
          { icon: 'ruler', label: 'Sq. Ft.', value: formatSqft(featured) },
        ] as const
      ).filter((s) => Boolean(s.value)),
    [featured],
  );

  return (
    <section className="ffp-section">
      <div className="ffp-container">
        <div ref={cardRef} className="reveal-fade-up ffp-card glow-trace-wrap">
          <GlowTrace delay={0.4} />
          <div className="ffp-media-wrap">
            {/*
              Main image is wrapped in whichever slot is currently active so
              the editor's media picker opens the right slot when the author
              clicks the big preview. Keep every slot wrapper mounted (hidden
              siblings) so each remains editable from the sidebar as well.
            */}
            <div className="ffp-media">
              {/*
                Four inline wrappers — one per image slot — so the editor's
                media picker opens the correct slot when the author clicks the
                main image, and the schema linter sees each groupPath as a
                string literal. Only the wrapper matching the active slot
                mounts at a time; the empty-state fallback targets slot 1.
              */}
              {current && current.slotIndex === 0 && (
                <EditableSlotGroup
                  groupPath="featuredfloorplan.image_1"
                  definition={image1Def}
                  className="ffp-media-active"
                >
                  <VibesitesImage
                    url={current.src}
                    alt={current.alt}
                    className="ffp-media-image"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </EditableSlotGroup>
              )}
              {current && current.slotIndex === 1 && (
                <EditableSlotGroup
                  groupPath="featuredfloorplan.image_2"
                  definition={image2Def}
                  className="ffp-media-active"
                >
                  <VibesitesImage
                    url={current.src}
                    alt={current.alt}
                    className="ffp-media-image"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </EditableSlotGroup>
              )}
              {current && current.slotIndex === 2 && (
                <EditableSlotGroup
                  groupPath="featuredfloorplan.image_3"
                  definition={image3Def}
                  className="ffp-media-active"
                >
                  <VibesitesImage
                    url={current.src}
                    alt={current.alt}
                    className="ffp-media-image"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </EditableSlotGroup>
              )}
              {current && current.slotIndex === 3 && (
                <EditableSlotGroup
                  groupPath="featuredfloorplan.image_4"
                  definition={image4Def}
                  className="ffp-media-active"
                >
                  <VibesitesImage
                    url={current.src}
                    alt={current.alt}
                    className="ffp-media-image"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </EditableSlotGroup>
              )}
              {!current && (
                <EditableSlotGroup
                  groupPath="featuredfloorplan.image_1"
                  definition={image1Def}
                  className="ffp-media-active"
                >
                  <div className="ffp-media-placeholder" aria-hidden="true" />
                </EditableSlotGroup>
              )}

              {tourUrl && (
                <EditableSlotGroup
                  groupPath="featuredfloorplan.tour_cta"
                  definition={tourCtaDef}
                  className="ffp-tour-btn-wrap"
                >
                  <button
                    type="button"
                    className="ffp-tour-btn"
                    onClick={() => setTourOpen(true)}
                    aria-label={tourLabel}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span>{tourLabel}</span>
                  </button>
                </EditableSlotGroup>
              )}
            </div>

            {/* Thumbnail picker — only shown when 2+ images are filled. */}
            {images.length > 1 && (
              <div className="ffp-thumbs" role="tablist" aria-label="Floor plan images">
                {images.map((img, i) => (
                  <button
                    key={`${img.src}-${i}`}
                    type="button"
                    role="tab"
                    aria-selected={i === activeImage}
                    aria-label={img.alt || `Image ${i + 1}`}
                    className={`ffp-thumb${i === activeImage ? ' ffp-thumb--active' : ''}`}
                    onClick={() => setActiveImage(i)}
                  >
                    <VibesitesImage
                      url={img.src}
                      alt=""
                      className="ffp-thumb-image"
                      sizes="90px"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ffp-body">
            <EditableSlotGroup
              groupPath="featuredfloorplan.details"
              definition={detailsDef}
              className="ffp-headline-wrap"
            >
              <div className="ffp-headline">
                <h3 className="ffp-name">{planName}</h3>
                {planCode ? <p className="ffp-code">Plan {planCode}</p> : null}
                <p className="ffp-price">{price}</p>
              </div>
            </EditableSlotGroup>

            <div ref={statsRef} className="ffp-stats">
              {stats.map((stat) => (
                <div key={stat.icon} className="ffp-stat reveal-fade-up">
                  <div className="ffp-stat-icon" aria-hidden="true">
                    {stat.icon === 'bed' && (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 14v6" />
                        <path d="M22 14v6" />
                        <path d="M2 14h20" />
                        <path d="M4 14V9a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5" />
                        <path d="M8 11h3a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                    {stat.icon === 'bath' && (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12h18" />
                        <path d="M4 12v4a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-4" />
                        <path d="M6 12V7a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v1" />
                        <path d="M7 19l-1 2" />
                        <path d="M17 19l1 2" />
                      </svg>
                    )}
                    {stat.icon === 'ruler' && (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7l14 14 4-4L7 3z" />
                        <path d="M8 8l1.5 1.5" />
                        <path d="M11 11l1.5 1.5" />
                        <path d="M14 14l1.5 1.5" />
                      </svg>
                    )}
                  </div>
                  <div className="ffp-stat-text">
                    <span className="ffp-stat-value">{stat.value}</span>
                    <span className="ffp-stat-label">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>

            <RichTextContent
              path="featuredfloorplan.description"
              as="div"
              className="ffp-description"
            />

            <div ref={ctaRef} className="reveal-fade-up ffp-actions">
              <EditableSlotGroup
                groupPath="featuredfloorplan.view_all_cta"
                definition={viewAllCtaDef}
              >
                <a
                  href={viewAllCta.url || '/floor-plans'}
                  target={viewAllCta._target || '_self'}
                  className="ffp-cta ffp-cta--primary"
                >
                  <span>{viewAllCta.label || 'View All Floor Plans'}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="featuredfloorplan.schedule_cta"
                definition={scheduleCtaDef}
              >
                <a
                  href={scheduleCta.url || '/schedule-a-tour'}
                  target={scheduleCta._target || '_self'}
                  className="ffp-cta ffp-cta--ghost"
                >
                  <span>{scheduleCta.label || 'Schedule A Tour'}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      {/*
        Hidden schema-lint anchors — ensure every slot the component owns is
        discoverable by the editor sidebar and the static schema linter, even
        when the matching JSX branch isn't currently mounted (empty slot, non-
        active image, etc.).
      */}
      <div className="hidden" style={{ display: 'none' }}>
        <EditableSlotGroup groupPath="featuredfloorplan.image_1" definition={image1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredfloorplan.image_2" definition={image2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredfloorplan.image_3" definition={image3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredfloorplan.image_4" definition={image4Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="featuredfloorplan.tour_cta" definition={tourCtaDef}><span /></EditableSlotGroup>
      </div>

      {/*
        3D-tour lightbox — mounts in-place (no portal) with a fixed overlay
        above everything. Click outside, press Escape, or hit the close button
        to dismiss. Matches the pattern used by Gallery.tsx so these two
        overlays feel like siblings on sites that use both.
      */}
      {tourOpen && tourUrl && (
        <div
          className="ffp-tour-overlay"
          onClick={closeTour}
          role="dialog"
          aria-modal="true"
          aria-label={tourLabel}
        >
          <button
            type="button"
            onClick={closeTour}
            className="ffp-tour-close"
            aria-label="Close 3D tour"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div
            className="ffp-tour-frame-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={tourUrl}
              title={tourLabel}
              className="ffp-tour-frame"
              allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen; vr"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <style>{`
        .ffp-section {
          padding: 3.5rem 0 4rem;
          background: var(--vibe-color-background, #ffffff);
        }
        @media (min-width: 768px) {
          .ffp-section { padding: 5rem 0 6rem; }
        }

        .ffp-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ffp-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .ffp-container { padding: 0 2rem; } }

        .ffp-card {
          position: relative;
          display: grid;
          grid-template-columns: 1fr;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 14px);
          overflow: hidden;
          box-shadow: 0 10px 36px rgba(0, 0, 0, 0.06);
        }
        @media (min-width: 1024px) {
          .ffp-card {
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
          }
        }

        .ffp-media-wrap {
          display: flex;
          flex-direction: column;
          background: #f5f5f6;
          border-bottom: 1px solid #ececef;
        }
        @media (min-width: 1024px) {
          .ffp-media-wrap {
            border-bottom: none;
            border-right: 1px solid #ececef;
          }
        }

        .ffp-media {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          aspect-ratio: 4 / 3;
          min-height: 280px;
          padding: 1.5rem;
        }
        @media (min-width: 1024px) {
          .ffp-media {
            flex: 1;
            aspect-ratio: auto;
            padding: 2.5rem;
          }
        }

        .ffp-media-active {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        .ffp-media-image {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
        }

        .ffp-media-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
          border-radius: var(--vibe-radius-md, 8px);
        }

        /* 3D-tour trigger button, pinned bottom-right of the media area. */
        .ffp-tour-btn-wrap {
          position: absolute;
          bottom: 0.85rem;
          right: 0.85rem;
          z-index: 5;
        }

        .ffp-tour-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.55rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #ffffff;
          background: rgba(17, 17, 17, 0.72);
          border: 1px solid rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          border-radius: 9999px;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .ffp-tour-btn:hover {
          background: rgba(17, 17, 17, 0.9);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
        }
        .ffp-tour-btn svg {
          flex-shrink: 0;
        }

        /* Thumbnail picker strip under the main image. */
        .ffp-thumbs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding: 0.75rem 1rem 1rem;
          justify-content: center;
          background: #f5f5f6;
        }
        @media (min-width: 1024px) {
          .ffp-thumbs { padding: 1rem 2rem 1.25rem; }
        }

        .ffp-thumb {
          width: 68px;
          height: 68px;
          padding: 0;
          background: #ffffff;
          border: 2px solid transparent;
          border-radius: var(--vibe-radius-sm, 8px);
          cursor: pointer;
          overflow: hidden;
          transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
          flex-shrink: 0;
        }
        @media (min-width: 640px) {
          .ffp-thumb { width: 80px; height: 80px; }
        }
        .ffp-thumb:hover {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 50%, transparent);
        }
        .ffp-thumb--active {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 4px 12px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }

        .ffp-thumb-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 4px;
          display: block;
        }

        /* 3D-tour lightbox overlay. */
        .ffp-tour-overlay {
          position: fixed;
          inset: 0;
          z-index: 60;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .ffp-tour-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          z-index: 2;
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          background: rgba(0, 0, 0, 0.55);
          border: none;
          border-radius: 9999px;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .ffp-tour-close:hover {
          background: rgba(0, 0, 0, 0.8);
        }

        .ffp-tour-frame-wrap {
          position: relative;
          width: 100%;
          max-width: 1100px;
          aspect-ratio: 16 / 10;
          max-height: 90vh;
          background: #000;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
        }

        .ffp-tour-frame {
          width: 100%;
          height: 100%;
          border: 0;
          display: block;
        }

        .ffp-body {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 2rem 1.75rem;
        }
        @media (min-width: 768px) {
          .ffp-body { padding: 2.5rem 2.5rem; }
        }
        @media (min-width: 1024px) {
          .ffp-body { padding: 3rem 3rem; }
        }

        .ffp-headline-wrap {
          display: block;
        }

        .ffp-headline {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .ffp-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.625rem;
          font-weight: 700;
          line-height: 1.2;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          margin: 0;
        }
        @media (min-width: 768px) {
          .ffp-name { font-size: 1.875rem; }
        }

        .ffp-code {
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .ffp-price {
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0.35rem 0 0;
        }

        .ffp-stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.5rem;
          padding: 1rem 0.85rem;
          background: #f7f7f8;
          border: 1px solid #ececef;
          border-radius: var(--vibe-radius-md, 10px);
        }
        @media (min-width: 640px) {
          .ffp-stats { padding: 1.25rem; gap: 0.85rem; }
        }

        /*
         * Keep stats always stacked (icon on top, value + label below). This
         * gives the longest value ("610 Sq. Ft.") the full column width so it
         * never wraps mid-text, regardless of whether the card body is in a
         * narrow split layout or a wider single column.
         */
        .ffp-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0.4rem;
          min-width: 0;
        }

        .ffp-stat-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          color: var(--vibe-color-primary);
          flex-shrink: 0;
        }

        .ffp-stat-text {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 0;
          width: 100%;
        }

        .ffp-stat-value {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          line-height: 1.2;
          white-space: nowrap;
        }

        .ffp-stat-label {
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          margin-top: 0.2rem;
          white-space: nowrap;
        }
        @media (min-width: 640px) {
          .ffp-stat-label { font-size: 0.72rem; }
        }

        .ffp-description {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: var(--vibe-color-text);
        }
        .ffp-description p { margin: 0 0 0.75rem; }
        .ffp-description p:last-child { margin-bottom: 0; }

        .ffp-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.65rem;
          margin-top: 0.5rem;
        }

        .ffp-cta {
          flex: 1 1 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.4rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          text-decoration: none;
          border-radius: 9999px;
          border: 2px solid transparent;
          white-space: nowrap;
          transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease;
        }
        @media (min-width: 1280px) {
          .ffp-cta { padding: 0.85rem 1.6rem; font-size: 0.76rem; letter-spacing: 0.12em; }
        }

        .ffp-cta--primary {
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 82%, black) 100%);
          border-color: var(--vibe-color-secondary);
        }
        .ffp-cta--primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px color-mix(in srgb, var(--vibe-color-secondary) 32%, transparent);
        }

        .ffp-cta--ghost {
          color: var(--vibe-color-primary);
          background: transparent;
          border-color: color-mix(in srgb, var(--vibe-color-primary) 55%, transparent);
        }
        .ffp-cta--ghost:hover {
          transform: translateY(-2px);
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, transparent);
          border-color: var(--vibe-color-primary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeaturedFloorPlan',
  displayName: 'Featured Floor Plan',
  tokens: {
    'featuredfloorplan.description': {
      label: 'Floor Plan Description',
      type: 'richtext',
      defaultValue:
        '<p>Thoughtful design meets everyday comfort in this signature layout. Open living spaces, premium finishes, and large windows combine to create a bright, welcoming home — ideal for relaxing nights in or hosting friends on the weekend.</p>',
      description:
        'A short paragraph describing why this floor plan stands out — views, finishes, layout highlights, etc.',
      generator: {
        prompt:
          'A 2-3 sentence narrative describing the standout qualities of this featured floor plan: layout flow, natural light, storage, and lifestyle fit. Mention the property name once if it reads naturally. Avoid repeating the hard spec numbers (beds/baths/sqft) that appear in the stats row.',
        context:
          'Shown next to the floor plan image, below the stats row. Keep it warm, descriptive, and short.',
        length: '2-3 sentences',
      },
    },
  },
  slotGroups: {
    'featuredfloorplan.image_1': image1Def,
    'featuredfloorplan.image_2': image2Def,
    'featuredfloorplan.image_3': image3Def,
    'featuredfloorplan.image_4': image4Def,
    'featuredfloorplan.details': detailsDef,
    'featuredfloorplan.schedule_cta': scheduleCtaDef,
    'featuredfloorplan.view_all_cta': viewAllCtaDef,
    'featuredfloorplan.tour_cta': tourCtaDef,
  },
  collectionBindings: {
    'featuredfloorplan.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description:
        'Floor plan collection used to auto-fill the featured image, name, specs, and starting price. The first plan with available units is spotlighted; otherwise the first plan on the property.',
    },
  },
};

(FeaturedFloorPlan as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FeaturedFloorPlan;
